import React, { useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { canEditProfile } from "../utils/permissions";
import { ProfileService } from "../services/ProfileService";
import Layout from "../components/Layout";

export default function Profile() {
  const { username } = useParams();
  const { user, userType, updateUserProfile } = useContext(AuthContext);
  
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [profileData, setProfileData] = useState({
    username: username,
    name: user?.name || username,
    bio: user?.bio || "No bio available",
    articles: []
  });
  
  const isOwnProfile = user && user.username === username;
  const canEdit = isOwnProfile && canEditProfile(userType);
  
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const data = await ProfileService.getProfile();
        
        // Update form fields with profile data
        setName(data.name || user.name || "");
        setBio(data.bio || "");
        
        // Update profile data
        setProfileData({
          username: username,
          name: data.name || user.name || username,
          bio: data.bio || "No bio available",
          articles: data.articles || []
        });
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [user, username, isEditing]);
  
  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setSaveError(null);
      
      await ProfileService.updateProfile({
        name,
        bio
      });
      
      // Update local user state
      updateUserProfile({
        name,
        bio
      });
      
      // Update profile data
      setProfileData({
        ...profileData,
        name,
        bio
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving profile:", error);
      setSaveError("Failed to save profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 max-w-4xl mx-auto">
        {loading && !isEditing ? (
          <div className="flex justify-center py-8">
            <div className="text-gray-500">Loading profile...</div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8">
            {isEditing ? (
              <div className="space-y-4">
                <h1 className="text-3xl font-serif mb-6">Edit Profile</h1>
                
                {saveError && (
                  <div className="p-3 bg-red-100 text-red-700 rounded mb-4">
                    {saveError}
                  </div>
                )}
                
                <div>
                  <label className="block mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full p-2 border rounded"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label className="block mb-1">Bio</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    className="w-full p-2 border rounded"
                    rows="4"
                    disabled={loading}
                  ></textarea>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 bg-taupe text-white rounded disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Profile"}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 bg-gray-200 rounded"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-start">
                  <h1 className="text-3xl font-serif mb-2">{profileData.name}</h1>
                  {canEdit && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                    >
                      Edit Profile
                    </button>
                  )}
                </div>
                
                <p className="text-gray-500 mb-6">@{profileData.username}</p>
                
                <div className="mb-8">
                  <h2 className="text-lg font-medium mb-2">About</h2>
                  <p className="text-gray-700">{profileData.bio}</p>
                </div>
                
                <div>
                  <h2 className="text-lg font-medium mb-4">Articles</h2>
                  {profileData.articles && profileData.articles.length > 0 ? (
                    <div className="space-y-4">
                      {profileData.articles.map(article => (
                        <div key={article.slug} className="border-b pb-2">
                          <h3 className="font-medium">{article.title}</h3>
                          <p className="text-sm text-gray-500">{article.summary}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No articles yet.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}